import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
// Self-hosted fonts — no pre-consent requests to Google (GDPR #2)
import '@fontsource-variable/inter/wght.css';           // Inter Variable, all weights, latin + latin-ext
import './index.css';
import 'leaflet/dist/leaflet.css'; // Import Leaflet CSS at top level to ensure it loads before components render
import App from './App.tsx';
import { I18nProvider } from './lib/i18n';
import { SessionProvider } from './providers/AuthProvider';
import { ThemeProvider } from './components/ui/ThemeProvider';
import { ErrorFallback } from './components/ui/ErrorFallback';

// Initialize Capacitor StatusBar on native only — on web the plugin
// rejects with "not implemented" and spammar konsolen
if ((window as any).Capacitor?.isNativePlatform?.()) {
  import('@capacitor/status-bar')
    .then(async ({ StatusBar, Style }) => {
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#F9F9FB' });
    })
    .catch(() => {});
}

// Dev-only: role switcher — tree-shaken out of production builds
const RoleSwitcher = import.meta.env.DEV
  ? (await import('./components/dev/RoleSwitcher')).default
  : null;

import { initSentry, captureException } from './lib/sentry';

class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error; errorInfo?: React.ErrorInfo }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('UI ErrorBoundary caught an error:', error, errorInfo);
    
    try {
      captureException(error, {
        componentStack: errorInfo.componentStack,
        endpoint: window.location.pathname,
        timestamp: new Date().toISOString(),
        type: 'react-error-boundary',
      });
    } catch (e) {
      console.warn('Failed to report error to Sentry:', e);
    }
    
    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <ErrorFallback
          error={this.state.error}
          resetErrorBoundary={this.handleReset}
          errorInfo={this.state.errorInfo}
        />
      );
    }
    return this.props.children;
  }
}

initSentry();

// Global error handlers
window.addEventListener('error', (e) => {
  console.error('Global error:', e.error || e.message || e);
  const err = e.error instanceof Error ? e.error : new Error(String(e.message || e));
  captureException(err, { endpoint: window.location.pathname, timestamp: new Date().toISOString(), type: 'global-error' });
});

window.addEventListener('unhandledrejection', (e) => {
  console.error('Unhandled promise rejection:', e.reason || e);
  const err = e.reason instanceof Error ? e.reason : new Error(String(e.reason ?? 'unhandled rejection'));
  captureException(err, { endpoint: window.location.pathname, timestamp: new Date().toISOString(), type: 'unhandledrejection' });
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  console.error('❌ CRITICAL: Root element not found!');
  document.body.innerHTML = '<div style="padding: 20px; color: red;"><h1>Error: Root element not found</h1><p>Make sure index.html has a &lt;div id="root"&gt;&lt;/div&gt; element.</p></div>';
} else {
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <AppErrorBoundary>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background text-foreground">Indlæser…</div>}>
          <ThemeProvider>
            <I18nProvider>
              <SessionProvider>
                <App />
                {RoleSwitcher && <RoleSwitcher />}
              </SessionProvider>
            </I18nProvider>
          </ThemeProvider>
        </Suspense>
      </AppErrorBoundary>
    </React.StrictMode>
  );
}
